import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

export const SmoothScroll = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;

    // Start fresh page mounts at the top, unless we're deep-linking to a
    // homepage section (hash or /about,/products,/quote,/contact).
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const SECTION_PATHS = ["/about", "/products", "/quote", "/contact"];
    if (!window.location.hash && !SECTION_PATHS.includes(window.location.pathname)) {
      window.scrollTo(0, 0);
      lenis.scrollTo(0, { immediate: true, force: true });
    }

    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // anchor handling (same-page hash links like href="#about")
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (id.length > 1) {
        e.preventDefault();
        const el = document.querySelector(id);
        if (el) lenis.scrollTo(el, { offset: -10, duration: 1.4 });
      }
    };
    document.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      if (window.__lenis === lenis) delete window.__lenis;
      lenis.destroy();
    };
  }, []);

  // Scroll to hash on mount AND on subsequent client-side navigation to /#section.
  // Also supports dedicated section paths (/about, /products, /quote, /contact)
  // which render the homepage and land on the matching section.
  useEffect(() => {
    const SECTION_PATHS = {
      "/about": "#about",
      "/products": "#products",
      "/quote": "#quote",
      "/contact": "#contact",
    };
    const hash = location.hash || SECTION_PATHS[location.pathname] || "";
    if (!hash || hash.length <= 1) return;

    let cancelled = false;
    let raf;
    const timers = [];

    const onUserScroll = () => { cancelled = true; };
    window.addEventListener("wheel", onUserScroll, { passive: true });
    window.addEventListener("touchstart", onUserScroll, { passive: true });
    window.addEventListener("keydown", onUserScroll);

    const scrollTo = (el, animate) => {
      const lenis = window.__lenis;
      if (lenis) lenis.scrollTo(el, { offset: -10, duration: animate ? 1.1 : 0, immediate: !animate });
      else el.scrollIntoView({ behavior: animate ? "smooth" : "auto" });
    };

    let waitAttempts = 0;
    const findAndStart = () => {
      if (cancelled) return;
      const el = document.querySelector(hash);
      waitAttempts += 1;
      if (el) {
        scrollTo(el, false);
        pin();
        return;
      }
      if (waitAttempts < 40) timers.push(setTimeout(findAndStart, 80));
    };

    // Keep the section pinned to the top until its position holds steady.
    const start = performance.now();
    let stable = 0;
    let lastTop = null;
    const pin = () => {
      raf = requestAnimationFrame(() => {
        if (cancelled) return;
        const el = document.querySelector(hash);
        if (!el) return;
        const top = Math.round(el.getBoundingClientRect().top);
        if (lastTop !== null && Math.abs(top - lastTop) <= 1) stable += 1;
        else stable = 0;
        lastTop = top;
        // Re-snap if drifted more than a hair from the -10 offset target.
        if (Math.abs(top + 10) > 4) scrollTo(el, false);
        if (stable < 5 && performance.now() - start < 3000) pin();
      });
    };

    timers.push(setTimeout(findAndStart, 100));
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      window.removeEventListener("wheel", onUserScroll);
      window.removeEventListener("touchstart", onUserScroll);
      window.removeEventListener("keydown", onUserScroll);
    };
  }, [location.pathname, location.hash]);

  return children;
};
